import { useCallback, useEffect, useRef, useState } from 'react'
import {
  firstClosedCandleTime,
  mergeCandleSeries,
  type AmmTelemetryView,
  type AmmVenueRef,
  type AmmVenueState,
  type CandleInterval,
  type CandleSeries,
  type Instrument,
  type InstrumentProfile,
  type InstrumentTelemetryView,
  type LedgerRef,
  type PairListing,
  type Rate,
  type TradeFeed,
  type VenueFeed,
} from '@nutrimatic/sdk'

import { nutrimaticErrorMessage } from '@/data/nutrimatic/errors'
import { fetchNutrimaticServiceReady, type NutrimaticServiceReady } from '@/data/nutrimatic/ready'
import { getNutrimaticSdk } from '@/data/nutrimatic/sdk'

export const NUTRIMATIC_CHART_INTERVALS: readonly CandleInterval[] = [
  '15m',
  '1h',
  '4h',
  '1d',
  '1w',
]

export type LiveStreamStatus = 'idle' | 'connecting' | 'live' | 'error'

export type NutrimaticPairDetail = {
  loading: boolean
  activityLoading: boolean
  liveStatus: LiveStreamStatus
  liveAsOf: number | null
  spot: Rate | null
  chart: CandleSeries | null
  trades: TradeFeed | null
  liveTradesHead: TradeFeed | null
  liquidity: VenueFeed | null
  pool: AmmTelemetryView | null
  basePrice: InstrumentTelemetryView | null
  quotePrice: InstrumentTelemetryView | null
  baseProfile: InstrumentProfile | null
  quoteProfile: InstrumentProfile | null
  ammState: AmmVenueState | null
  spotError: string | null
  chartError: string | null
  tradesError: string | null
  liquidityError: string | null
  poolError: string | null
  basePriceError: string | null
  quotePriceError: string | null
  baseProfileError: string | null
  quoteProfileError: string | null
  ammStateError: string | null
  loadingOlder: boolean
  loadOlderError: string | null
}

export type NutrimaticCatalogSnapshot = {
  loading: boolean
  ledgers: readonly LedgerRef[]
  instruments: readonly Instrument[]
  venues: readonly AmmVenueRef[]
  ledgersError: string | null
  instrumentsError: string | null
  venuesError: string | null
}

/** Pool summary row for Explore — listing + optional one-shot pool view. */
export type NutrimaticPoolRow = {
  listing: PairListing
  poolAddress: string | undefined
  view: AmmTelemetryView | null
  base: Instrument | undefined
  quote: Instrument | undefined
}

const emptyDetail = (): NutrimaticPairDetail => ({
  loading: false,
  activityLoading: false,
  liveStatus: 'idle',
  liveAsOf: null,
  spot: null,
  chart: null,
  trades: null,
  liveTradesHead: null,
  liquidity: null,
  pool: null,
  basePrice: null,
  quotePrice: null,
  baseProfile: null,
  quoteProfile: null,
  ammState: null,
  spotError: null,
  chartError: null,
  tradesError: null,
  liquidityError: null,
  poolError: null,
  basePriceError: null,
  quotePriceError: null,
  baseProfileError: null,
  quoteProfileError: null,
  ammStateError: null,
  loadingOlder: false,
  loadOlderError: null,
})

function settledValue<T>(
  result: PromiseSettledResult<T>,
): { value: T | null; error: string | null } {
  if (result.status === 'fulfilled') {
    return { value: result.value, error: null }
  }
  return { value: null, error: nutrimaticErrorMessage(result.reason) }
}

function requireSdk() {
  const sdk = getNutrimaticSdk()
  if (!sdk) throw new Error('Nutrimatic SDK not configured')
  return sdk
}

/**
 * Lab-style Nutrimatic market hub: catalog + listPairs + watchPair + activity.
 * Sole source of truth for the DEX vitrina (not for swap/LP execution).
 */
export function useNutrimaticMarketHub() {
  const [ready, setReady] = useState<NutrimaticServiceReady | null>(null)
  const [readyError, setReadyError] = useState<string | null>(null)

  const [pairs, setPairs] = useState<readonly PairListing[]>([])
  const [pairsLoading, setPairsLoading] = useState(true)
  const [pairsError, setPairsError] = useState<string | null>(null)
  const [selected, setSelected] = useState<PairListing | null>(null)

  const [poolRows, setPoolRows] = useState<readonly NutrimaticPoolRow[]>([])
  const [poolRowsLoading, setPoolRowsLoading] = useState(false)

  const [chartInterval, setChartInterval] = useState<CandleInterval>('1h')
  const [chartLimit, setChartLimit] = useState(100)
  const [tradeLimit, setTradeLimit] = useState(20)
  const [lpLimit, setLpLimit] = useState(20)

  const [detail, setDetail] = useState<NutrimaticPairDetail>(emptyDetail)
  const [liveEpoch, setLiveEpoch] = useState(0)
  const [activityEpoch, setActivityEpoch] = useState(0)
  const [catalog, setCatalog] = useState<NutrimaticCatalogSnapshot>({
    loading: true,
    ledgers: [],
    instruments: [],
    venues: [],
    ledgersError: null,
    instrumentsError: null,
    venuesError: null,
  })

  const liveGen = useRef(0)
  const activityGen = useRef(0)
  const pairsGen = useRef(0)
  const catalogGen = useRef(0)
  const poolRowsGen = useRef(0)
  const chartParams = useRef({ interval: chartInterval, chartLimit })
  chartParams.current = { interval: chartInterval, chartLimit }

  const refreshReady = useCallback(async () => {
    try {
      setReadyError(null)
      setReady(await fetchNutrimaticServiceReady())
    } catch (e) {
      setReady(null)
      setReadyError(nutrimaticErrorMessage(e))
    }
  }, [])

  const refreshCatalog = useCallback(async () => {
    const gen = ++catalogGen.current
    setCatalog((c) => ({
      ...c,
      loading: true,
      ledgersError: null,
      instrumentsError: null,
      venuesError: null,
    }))
    try {
      const sdk = requireSdk()
      const [ledgersR, instrumentsR, venuesR] = await Promise.allSettled([
        sdk.catalog.listLedgers(),
        sdk.catalog.listInstruments(),
        sdk.catalog.listVenues(),
      ])
      if (gen !== catalogGen.current) return
      const ledgers = settledValue(ledgersR)
      const instruments = settledValue(instrumentsR)
      const venues = settledValue(venuesR)
      setCatalog({
        loading: false,
        ledgers: ledgers.value ?? [],
        instruments: instruments.value ?? [],
        venues: venues.value ?? [],
        ledgersError: ledgers.error,
        instrumentsError: instruments.error,
        venuesError: venues.error,
      })
    } catch (e) {
      if (gen !== catalogGen.current) return
      const err = nutrimaticErrorMessage(e)
      setCatalog({
        loading: false,
        ledgers: [],
        instruments: [],
        venues: [],
        ledgersError: err,
        instrumentsError: err,
        venuesError: err,
      })
    }
  }, [])

  const refreshPairs = useCallback(async () => {
    const gen = ++pairsGen.current
    setPairsLoading(true)
    setPairsError(null)
    try {
      const list = await requireSdk().easy.read.listPairs()
      if (gen !== pairsGen.current) return
      setPairs(list)
      setSelected((prev) => {
        if (list.length === 0) return null
        if (!prev) return list[0] ?? null
        const match = list.find((p) => p.pairLabel === prev.pairLabel)
        if (match && match.venueId === prev.venueId) return prev
        return match ?? list[0] ?? null
      })
    } catch (e) {
      if (gen !== pairsGen.current) return
      setPairsError(nutrimaticErrorMessage(e))
      setPairs([])
      setSelected(null)
    } finally {
      if (gen === pairsGen.current) setPairsLoading(false)
    }
  }, [])

  /** Enrich listings with getPoolView for Explore tables. */
  useEffect(() => {
    if (!pairs.length || catalog.loading) {
      setPoolRows([])
      return
    }
    const gen = ++poolRowsGen.current
    setPoolRowsLoading(true)
    const sdk = getNutrimaticSdk()
    if (!sdk) {
      setPoolRowsLoading(false)
      return
    }
    const instrumentById = new Map(catalog.instruments.map((i) => [i.id, i]))
    const venueById = new Map(catalog.venues.map((v) => [v.id, v]))

    void Promise.all(
      pairs.map(async (listing) => {
        let view: AmmTelemetryView | null = null
        try {
          view = await sdk.easy.read.getPoolView({ venue: listing.venueId })
        } catch {
          view = null
        }
        const venue = venueById.get(listing.venueId)
        return {
          listing,
          poolAddress: venue?.poolAddress?.toLowerCase(),
          view,
          base: instrumentById.get(listing.baseInstrumentId),
          quote: instrumentById.get(listing.quoteInstrumentId),
        } satisfies NutrimaticPoolRow
      }),
    ).then((rows) => {
      if (gen !== poolRowsGen.current) return
      setPoolRows(rows)
      setPoolRowsLoading(false)
    })
  }, [pairs, catalog.loading, catalog.instruments, catalog.venues])

  useEffect(() => {
    void refreshReady()
    void refreshPairs()
    void refreshCatalog()
  }, [refreshReady, refreshPairs, refreshCatalog])

  useEffect(() => {
    if (!selected) {
      liveGen.current += 1
      setDetail(emptyDetail())
      return
    }
    const sdk = getNutrimaticSdk()
    if (!sdk) {
      setDetail({
        ...emptyDetail(),
        liveStatus: 'error',
        spotError: 'SDK not configured',
      })
      return
    }

    const gen = ++liveGen.current
    const { interval: iv, chartLimit: lim } = chartParams.current
    const listing = selected

    setDetail((d) => ({
      ...d,
      loading: true,
      liveStatus: 'connecting',
      liveAsOf: null,
      // Drop previous interval series so we never feed mixed buckets to the chart.
      chart: null,
      spotError: null,
      chartError: null,
      poolError: null,
      ammStateError: null,
      basePriceError: null,
      quotePriceError: null,
      liveTradesHead: null,
      loadOlderError: null,
    }))

    const sub = sdk.live.watchPair({
      pair: listing.pairLabel,
      chart: { interval: iv, limit: lim },
      onUpdate: (snap) => {
        if (gen !== liveGen.current) return
        const tradeEvents = (snap.trades ?? []) as TradeFeed['events']
        setDetail((d) => ({
          ...d,
          loading: false,
          liveStatus: 'live',
          liveAsOf: snap.asOf,
          spot: (snap.spot as Rate | undefined) ?? d.spot,
          ammState: (snap.state as AmmVenueState | undefined) ?? d.ammState,
          pool: (snap.pool as AmmTelemetryView | undefined) ?? d.pool,
          basePrice:
            (snap.baseTelemetry as InstrumentTelemetryView | undefined) ?? d.basePrice,
          quotePrice:
            (snap.quoteTelemetry as InstrumentTelemetryView | undefined) ?? d.quotePrice,
          chart: snap.chart ?? d.chart,
          liveTradesHead: snap.trades
            ? {
                scopeId: snap.venueId,
                scopeKind: 'venue' as const,
                events: tradeEvents,
                ordering: 'asc' as const,
                asOf: snap.asOf,
              }
            : d.liveTradesHead,
          spotError: null,
          chartError: null,
          poolError: null,
          ammStateError: null,
          basePriceError: null,
          quotePriceError: null,
        }))
      },
      onError: (err) => {
        if (gen !== liveGen.current) return
        const msg = err.message
        setDetail((d) => ({
          ...d,
          loading: false,
          liveStatus: 'error',
          spotError: msg,
          chartError: d.chartError ?? msg,
          poolError: d.poolError ?? msg,
          ammStateError: d.ammStateError ?? msg,
          basePriceError: d.basePriceError ?? msg,
          quotePriceError: d.quotePriceError ?? msg,
        }))
      },
    })

    return () => {
      sub.unsubscribe()
      liveGen.current += 1
    }
  }, [selected, chartInterval, chartLimit, liveEpoch])

  useEffect(() => {
    if (!selected) {
      activityGen.current += 1
      setDetail((d) => ({
        ...d,
        activityLoading: false,
        trades: null,
        liquidity: null,
        baseProfile: null,
        quoteProfile: null,
        tradesError: null,
        liquidityError: null,
        baseProfileError: null,
        quoteProfileError: null,
      }))
      return
    }
    const sdk = getNutrimaticSdk()
    if (!sdk) return

    const gen = ++activityGen.current
    const listing = selected
    const tLim = tradeLimit
    const lLim = lpLimit

    setDetail((d) => ({
      ...d,
      activityLoading: true,
      trades: null,
      liquidity: null,
      tradesError: null,
      liquidityError: null,
      baseProfileError: null,
      quoteProfileError: null,
    }))

    void Promise.allSettled([
      sdk.easy.read.getTradeFeed({ pair: listing.pairLabel, limit: tLim }),
      sdk.telemetry.getVenueFeed(listing.venueId, {
        limit: lLim,
        kinds: ['liquidity_add', 'liquidity_remove'],
      }),
      sdk.catalog.getInstrumentProfile(listing.baseInstrumentId),
      sdk.catalog.getInstrumentProfile(listing.quoteInstrumentId),
    ]).then(([tradesR, liquidityR, baseProfileR, quoteProfileR]) => {
      if (gen !== activityGen.current) return
      const trades = settledValue(tradesR)
      const liquidity = settledValue(liquidityR)
      const baseProfile = settledValue(baseProfileR)
      const quoteProfile = settledValue(quoteProfileR)
      setDetail((d) => ({
        ...d,
        activityLoading: false,
        trades: trades.value,
        liquidity: liquidity.value,
        baseProfile: baseProfile.value,
        quoteProfile: quoteProfile.value,
        tradesError: trades.error,
        liquidityError: liquidity.error,
        baseProfileError: baseProfile.error,
        quoteProfileError: quoteProfile.error,
      }))
    })

    return () => {
      activityGen.current += 1
    }
  }, [selected, tradeLimit, lpLimit, activityEpoch])

  const loadOlderCandles = useCallback(async () => {
    if (!selected || !detail.chart?.candles.length) return
    const before = firstClosedCandleTime(detail.chart.candles)
    if (before === undefined) return

    const gen = liveGen.current
    setDetail((d) => ({ ...d, loadingOlder: true, loadOlderError: null }))

    try {
      const older = await requireSdk().easy.read.getChart({
        pair: selected.pairLabel,
        interval: chartParams.current.interval,
        limit: chartParams.current.chartLimit,
        before,
      })
      if (gen !== liveGen.current) return
      setDetail((d) => {
        if (!d.chart) {
          return { ...d, chart: older, loadingOlder: false, loadOlderError: null }
        }
        return {
          ...d,
          chart: mergeCandleSeries(d.chart, older),
          loadingOlder: false,
          loadOlderError: null,
        }
      })
    } catch (e) {
      if (gen !== liveGen.current) return
      setDetail((d) => ({
        ...d,
        loadingOlder: false,
        loadOlderError: nutrimaticErrorMessage(e),
      }))
    }
  }, [selected, detail.chart])

  const selectPairByLabel = useCallback(
    (pairLabel: string) => {
      const hit = pairs.find((p) => p.pairLabel === pairLabel)
      if (hit) setSelected(hit)
    },
    [pairs],
  )

  const selectPairByPoolAddress = useCallback(
    (pairAddress: string) => {
      const key = pairAddress.toLowerCase()
      const row = poolRows.find((r) => r.poolAddress === key)
      if (row) {
        setSelected(row.listing)
        return
      }
      const fromVenueId = pairs.find((p) => p.venueId.toLowerCase().endsWith(`:${key}`))
      if (fromVenueId) setSelected(fromVenueId)
    },
    [poolRows, pairs],
  )

  const findInstrumentByAddress = useCallback(
    (address: string) => {
      const key = address.toLowerCase()
      if (key === '0x0000000000000000000000000000000000000000' || key === 'native') {
        return catalog.instruments.find((i) => !i.onChainAddress)
      }
      return catalog.instruments.find((i) => i.onChainAddress?.toLowerCase() === key)
    },
    [catalog.instruments],
  )

  return {
    ready,
    readyError,
    serviceOk: ready?.ok === true,
    pairs,
    pairsLoading,
    pairsError,
    poolRows,
    poolRowsLoading,
    selected,
    setSelected,
    selectPairByLabel,
    selectPairByPoolAddress,
    findInstrumentByAddress,
    chartInterval,
    setChartInterval,
    chartLimit,
    setChartLimit,
    tradeLimit,
    setTradeLimit,
    lpLimit,
    setLpLimit,
    detail,
    catalog,
    loadOlderCandles,
    reloadActivity: () => setActivityEpoch((n) => n + 1),
    reloadChartWindow: () => setLiveEpoch((n) => n + 1),
    reloadServiceDiagnostics: () => {
      void refreshReady()
      void refreshCatalog()
      void refreshPairs()
    },
    reloadPairs: () => {
      void refreshPairs()
    },
  }
}

export type NutrimaticMarketHub = ReturnType<typeof useNutrimaticMarketHub>
